import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPhysioValues,
  updatePhysioValuesForUser,
  PhysioValueProps,
} from '@/api/physioValueService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

interface UpdatePhysioParams {
  userId: string;
  data: PhysioValueProps;
}

export const useAdminUserPhysio = (userId: string | null) => {
  const queryClient = useQueryClient();

  const { data: physioData, isLoading } = useQuery<PhysioValueProps>({
    queryKey: queryKeys.userPhysio(userId ?? undefined),
    queryFn: () => getPhysioValues(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

  const updatePhysioMutation = useMutation({
    mutationFn: ({ userId, data }: UpdatePhysioParams) => updatePhysioValuesForUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'physio'] });
      toaster.create({ title: 'Données physiologiques mises à jour', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: 'Échec de la mise à jour',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  return {
    physioData,
    isLoading,
    updatePhysio: updatePhysioMutation.mutate,
    isUpdating: updatePhysioMutation.isPending,
  };
};
